import { ChangeDetectionStrategy, Component, HostBinding, Input, OnChanges, OnInit } from '@angular/core';

import { EpcotConfig, Sitecore, StakeConfig } from '@frontend/sports/common/client-config-data-access';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { OddsDisplayFormat } from '@frontend/sports/types/models/user-settings';
import { SyncUserSettingsService } from '@frontend/sports/user/feature';
import { chunk } from 'lodash-es';

import { ITaxationInfoViewModel } from '../../../taxation/components/digital/taxation-info.component';
import { IBetslipResultSummaryViewModel } from '../../models/betslip-result-summary.model';
import { IRewardTokenBadge } from '../../models/reward-token-badge.model';
import { RewardTokenBadgeService } from '../../services/reward-token-badge.service';

@Component({
    selector: 'bs-digital-result-summary',
    templateUrl: './result-summary.component.html',
    styleUrls: ['./result-summary.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class ResultSummaryComponent implements OnInit, OnChanges {
    isTaxationInfoVisible = false;
    isInfoVisible = false;
    isStakeLimitsInfoDisplayed = false;
    isTurnOverTax: boolean;
    turnOverTaxationMessage: string | null;
    rewardTitle: string;

    @Input() viewModel: IBetslipResultSummaryViewModel;
    @Input() oddsDisplayFormat: OddsDisplayFormat;
    @Input() isQuickBetConfirmation = false;
    @Input() isTeaserBet = false;

    rewardsUsedRows: IRewardTokenBadge[][];
    private rewardUsedTitle = this.siteCore.crmTokens.confirmation.RewardUsed;
    private promotionUsedTitle = this.siteCore.crmTokens.confirmation.PromotionUsed;
    private isEpcotEnabled = this.epcotConfig.isEnabled;

    constructor(
        public siteCore: Sitecore,
        stakeConfig: StakeConfig,
        private userSettings: SyncUserSettingsService,
        private epcotConfig: EpcotConfig,
        private rewardTokenBadgeService: RewardTokenBadgeService,
    ) {
        this.isStakeLimitsInfoDisplayed = stakeConfig.showStakeLimitInfo;
        this.isTurnOverTax = this.userSettings.isTurnoverTaxApplied;
        this.rewardsUsedRows = [];
        this.rewardTitle = this.isEpcotEnabled ? this.promotionUsedTitle : this.rewardUsedTitle;
    }

    @HostBinding('class') className = 'reward-tokens-module';

    ngOnChanges({ viewModel }: ISimpleChanges<ResultSummaryComponent>): void {
        if (!viewModel?.currentValue) {
            return;
        }

        const groupedRewards = this.viewModel.usedRewardTokens.reduce((badges, current) => {
            const existingBadge = badges.find(
                (b) => b.typeData.tokenType === current.tokenType && b.typeData.rewardTargetType === current.rewardTargetType,
            );

            if (existingBadge) {
                existingBadge.count += 1;

                return badges;
            }

            const badge = this.rewardTokenBadgeService.getBadge(current, this.viewModel.freebetStake);

            if (!badge) {
                return badges;
            }

            return [...badges, badge];
        }, [] as IRewardTokenBadge[]);

        // We need to split the rewards into rows programatically so we can ensure that only 2 are shown per row without using a grid
        this.rewardsUsedRows = chunk(groupedRewards, 2);
    }

    ngOnInit(): void {
        const possiblewinValue =
            this.isTurnOverTax && !!this.viewModel?.taxation?.possibleWinningsGross
                ? this.viewModel.taxation.possibleWinningsGross
                : this.viewModel.possibleWinnings?.value;
        this.turnOverTaxationMessage =
            this.isTurnOverTax && !!possiblewinValue
                ? possiblewinValue.greaterThan(this.userSettings.configSettings.winningsTaxThreshold)
                    ? this.siteCore.betslip.messages.TurnoverAndWinningTax
                    : this.siteCore.betslip.messages.TurnoverTax
                : null;
    }

    winsInfoClick(): void {
        this.isInfoVisible = true;
    }

    displayTaxationInfo(): void {
        this.isTaxationInfoVisible = true;
    }

    buildTaxationPopupViewModel(): ITaxationInfoViewModel {
        if (!this.viewModel.taxation) {
            return <ITaxationInfoViewModel>{};
        }

        return {
            possibleWinningsGross: this.viewModel.taxation.possibleWinningsGross,
            possibleWinningsNet: this.viewModel.possibleWinnings.value,
            taxationAmount: this.viewModel.taxation.taxationValue?.amount || null,
            taxationRate: this.viewModel.taxation.taxationValue?.rate || null,
        };
    }
}
